import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { TankFilterValues } from '@components/Common/EntryFilters/TankSelectorControlled';
import { DateFilterValues } from '@components/Common/EntryFilters/DateFilterControlled';

// URL param keys
const PARAM_TERMINAL = 'terminal';
const PARAM_TANK = 'tank';
const PARAM_FROM = 'from';
const PARAM_TO = 'to';
const PARAM_GROUP_BY_REFERENCE = 'groupByRef';
const PARAM_GROUP_BY_LOADING = 'groupByLoad';
const PARAM_INCLUDE_INVOICING = 'invoicing';

export interface TankReportFilters {
  terminalId: string | null;
  tankId: string | null;
  startDate: string | null;
  endDate: string | null;
  groupByReference: boolean;
  groupByLoading: boolean;
  includeInvoicingInfo: boolean;
}

export interface UseTankReportFiltersResult {
  filters: TankReportFilters;
  tankFilter: TankFilterValues;
  dateFilter: DateFilterValues;
  setTankFilter: (value: TankFilterValues) => void;
  setDateFilter: (value: DateFilterValues) => void;
  setGroupByReference: (value: boolean) => void;
  setGroupByLoading: (value: boolean) => void;
  setIncludeInvoicingInfo: (value: boolean) => void;
  clearFilters: () => void;
}

/**
 * Hook to manage tank report filters via URL search params.
 *
 * URL format: /reports/tanks?terminal=xxx&tank=xxx&from=xxx&to=xxx&groupByRef=true&groupByLoad=true&invoicing=true
 */
export function useTankReportFilters(): UseTankReportFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values from URL
  const filters = useMemo<TankReportFilters>(() => {
    const terminalId = searchParams.get(PARAM_TERMINAL);
    const tankId = searchParams.get(PARAM_TANK);
    const startDate = searchParams.get(PARAM_FROM);
    const endDate = searchParams.get(PARAM_TO);
    const groupByReference = searchParams.get(PARAM_GROUP_BY_REFERENCE) === 'true';
    const groupByLoading = searchParams.get(PARAM_GROUP_BY_LOADING) === 'true';
    const includeInvoicingInfo = searchParams.get(PARAM_INCLUDE_INVOICING) === 'true';

    return {
      terminalId,
      tankId,
      startDate,
      endDate,
      groupByReference,
      groupByLoading,
      includeInvoicingInfo,
    };
  }, [searchParams]);

  // Tank filter for the controlled component
  const tankFilter = useMemo<TankFilterValues>(() => ({
    terminalId: filters.terminalId,
    tankId: filters.tankId,
  }), [filters.terminalId, filters.tankId]);

  // Date filter for the controlled component
  const dateFilter = useMemo<DateFilterValues>(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [filters.startDate, filters.endDate]);

  // Update tank filter in URL
  const setTankFilter = useCallback((value: TankFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.terminalId) {
        newParams.set(PARAM_TERMINAL, value.terminalId);
      } else {
        newParams.delete(PARAM_TERMINAL);
      }

      if (value.tankId) {
        newParams.set(PARAM_TANK, value.tankId);
      } else {
        newParams.delete(PARAM_TANK);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update date filter in URL
  const setDateFilter = useCallback((value: DateFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.startDate) {
        newParams.set(PARAM_FROM, value.startDate);
      } else {
        newParams.delete(PARAM_FROM);
      }

      if (value.endDate) {
        newParams.set(PARAM_TO, value.endDate);
      } else {
        newParams.delete(PARAM_TO);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update boolean options
  const setGroupByReference = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(PARAM_GROUP_BY_REFERENCE, 'true');
      } else {
        newParams.delete(PARAM_GROUP_BY_REFERENCE);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const setGroupByLoading = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(PARAM_GROUP_BY_LOADING, 'true');
      } else {
        newParams.delete(PARAM_GROUP_BY_LOADING);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const setIncludeInvoicingInfo = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(PARAM_INCLUDE_INVOICING, 'true');
      } else {
        newParams.delete(PARAM_INCLUDE_INVOICING);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    tankFilter,
    dateFilter,
    setTankFilter,
    setDateFilter,
    setGroupByReference,
    setGroupByLoading,
    setIncludeInvoicingInfo,
    clearFilters,
  };
}

export default useTankReportFilters;
